/**
 * CalendarEventContent
 *
 * Renderer customizado para o `eventContent` do FullCalendar.
 * Mantém o layout padrão (hora + título) e acrescenta ícones de lembrete
 * (`Bell`) e repetição (`Repeat`) com tooltip detalhando os valores.
 *
 * RELEASE:
 *   2026-06-24 v1.0.0 - Versão inicial (badges + tooltip para reminders/recurrence)
 *   2026-06-25 v1.1.0 - Bolinha colorida com a cor do calendário ao lado da hora/título
 */

import { Bell, Repeat } from 'lucide-react';
import type { EventContentArg } from '@fullcalendar/core';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

/** Estrutura mínima esperada de cada lembrete (JSONB do banco). */
interface ReminderItem {
  method?: string;
  minutes?: number;
}

/** Converte minutos antes do evento em rótulo PT-BR amigável. */
function formatReminderMinutes(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return `${minutes} min antes`;
  if (minutes < 60) return `${minutes} min antes`;
  if (minutes < 1440) {
    const h = minutes / 60;
    return `${Number.isInteger(h) ? h : h.toFixed(1)} h antes`;
  }
  const d = minutes / 1440;
  return `${Number.isInteger(d) ? d : d.toFixed(1)} dia${d === 1 ? '' : 's'} antes`;
}

/** Mapeia RRULE simples para rótulo PT-BR; fallback é a string crua. */
function formatRecurrenceRule(rule: string): string {
  const upper = rule.toUpperCase();
  if (upper.includes('FREQ=DAILY')) return 'Repete diariamente';
  if (upper.includes('FREQ=WEEKLY')) return 'Repete semanalmente';
  if (upper.includes('FREQ=MONTHLY')) return 'Repete mensalmente';
  if (upper.includes('FREQ=YEARLY')) return 'Repete anualmente';
  return `Repetição: ${rule}`;
}

interface CalendarEventContentProps {
  arg: EventContentArg;
}

export function CalendarEventContent({ arg }: CalendarEventContentProps) {
  // extendedProps carrega reminders/recurrence_rule injetados em Calendar.tsx
  const reminders = (arg.event.extendedProps?.reminders ?? []) as ReminderItem[];
  const recurrenceRule = (arg.event.extendedProps?.recurrenceRule ?? null) as
    | string
    | null;

  const reminderList = Array.isArray(reminders)
    ? reminders
        .map((r) => Number(r?.minutes))
        .filter((n) => Number.isFinite(n) && n > 0)
    : [];

  const hasReminders = reminderList.length > 0;
  const hasRecurrence = !!recurrenceRule;

  // Cor do calendário (definida em Calendar.tsx via backgroundColor/borderColor)
  const calendarColor =
    (arg.event.backgroundColor || arg.event.borderColor || '') as string;

  // v1.2.0: risco no título quando evento concluído ou cancelado
  const completedAt = arg.event.extendedProps?.completedAt as string | null | undefined;
  const cancelledAt = arg.event.extendedProps?.cancelledAt as string | null | undefined;
  const isFinished = !!completedAt || !!cancelledAt;

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-center gap-1 w-full overflow-hidden text-[0.72rem] leading-tight">
        {/* Bolinha com a cor do calendário, para identificação visual rápida */}
        {calendarColor && (
          <span
            className="shrink-0 inline-block h-2 w-2 rounded-full border border-black/10"
            style={{ backgroundColor: calendarColor }}
            aria-hidden="true"
          />
        )}

        {/* Hora (oculta em eventos all-day e na listWeek que já mostra) */}
        {arg.timeText && (
          <span className="opacity-90 shrink-0">{arg.timeText}</span>
        )}

        {/* Título truncado — risco quando concluído/cancelado (v1.2.0) */}
        <span
          className={
            'flex-1 min-w-0 truncate font-medium' +
            (isFinished ? ' line-through opacity-80' : '')
          }
        >
          {arg.event.title}
        </span>

        {/* Badge de lembretes */}
        {hasReminders && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="shrink-0 inline-flex items-center opacity-90"
                aria-label="Possui lembretes"
              >
                <Bell className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              <p className="font-medium mb-1">
                Lembrete{reminderList.length === 1 ? '' : 's'}
              </p>
              <ul className="space-y-0.5">
                {reminderList.map((m, i) => (
                  <li key={`${m}-${i}`}>• {formatReminderMinutes(m)}</li>
                ))}
              </ul>
            </TooltipContent>
          </Tooltip>
        )}

        {/* Badge de recorrência */}
        {hasRecurrence && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span
                className="shrink-0 inline-flex items-center opacity-90"
                aria-label="Evento recorrente"
              >
                <Repeat className="h-3 w-3" />
              </span>
            </TooltipTrigger>
            <TooltipContent side="top" className="text-xs">
              {formatRecurrenceRule(recurrenceRule!)}
            </TooltipContent>
          </Tooltip>
        )}
      </div>
    </TooltipProvider>
  );
}
